import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import CourseEnrollmentForm from '@/components/CourseEnrollmentForm';
import GeneralEnquiryForm from '@/components/GeneralEnquiryForm';
import { 
  User, 
  Award, 
  Calendar, 
  CreditCard, 
  Download, 
  BookOpen,
  Users,
  LogOut,
  Settings,
  Bell,
  CheckCircle,
  FileText,
  ExternalLink,
  Clock,
  MapPin,
  Star,
  Trophy,
  XCircle,
  MessageSquare,
  Key
} from 'lucide-react';

interface GeneralEnquiry {
  id: string;
  user_email: string;
  user_name: string;
  enquiry_type: string;
  subject: string;
  message: string;
  priority: string;
  status: string;
  admin_response: string;
  admin_notes: string;
  created_at: string;
  updated_at: string;
  resolved_at: string;
}

interface EnrollmentRequest {
  id: string;
  user_email: string;
  user_name: string;
  course_title: string;
  course_instructor: string;
  original_price: number;
  discounted_price: number;
  discount_percentage: number;
  membership_type: string;
  additional_notes: string;
  status: string;
  created_at: string;
}

interface MemberProfile {
  id: string;
  full_name: string;
  email: string;
  organization: string;
  position: string;
  phone: string;
  country: string;
  membership_status: string;
  payment_status: string;
  membership_start_date: string;
  membership_end_date: string;
  membership_types_2025_12_03_10_30: {
    name: string;
    price_eur: number;
    benefits: string[];
    discount_percentage: number;
    payment_link?: string;
  };
}

interface Resource {
  id: string;
  title: string;
  description: string;
  file_url: string;
  file_type: string;
  category: string;
  membership_types: string[];
  is_active: boolean;
}

interface Course {
  id: string;
  title: string;
  description: string;
  instructor: string;
  duration_hours: number;
  price_eur: number;
  discount_percentage: number;
  course_url: string;
  category: string;
  difficulty_level: string;
  membership_types: string[];
  is_active: boolean;
}

interface Event {
  id: string;
  title: string;
  description: string;
  event_date: string;
  location: string;
  event_type: string;
  registration_url: string;
  membership_types: string[];
  is_active: boolean;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  badge_image_url: string;
  criteria: string;
  points: number;
  category: string;
  is_active: boolean;
}

interface UserAchievement {
  id: string;
  achievement_id: string;
  earned_date: string;
  achievements_2025_12_03_10_30: Achievement;
}

interface Certificate {
  id: string;
  certificate_type: string;
  certificate_name: string;
  issue_date: string;
  certificate_url: string;
  verification_code: string;
}

interface Badge {
  id: string;
  badge_type: string;
  badge_name: string;
  badge_image_url: string;
  earned_date: string;
  description: string;
}

const EnhancedMemberDashboard = () => {
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [resources, setResources] = useState<Resource[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [userAchievements, setUserAchievements] = useState<UserAchievement[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [enrollmentRequests, setEnrollmentRequests] = useState<EnrollmentRequest[]>([]);
  const [generalEnquiries, setGeneralEnquiries] = useState<GeneralEnquiry[]>([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [profileLoading, setProfileLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { user, loading: authLoading, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !authLoading) {
      fetchProfile();
      fetchContent();
    }
  }, [user, authLoading]);

  // Auto-refresh content every 30 seconds to catch admin changes
  useEffect(() => {
    if (user && !authLoading) {
      const interval = setInterval(() => {
        fetchContent();
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [user, authLoading]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('member_profiles_2025_12_03_10_30')
        .select(`
          *,
          membership_types_2025_12_03_10_30 (
            name,
            price_eur,
            benefits,
            discount_percentage,
            payment_link
          )
        `)
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
      } else {
        setProfile(data);
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchContent = async () => {
    if (!user) return;

    try {
      // Fetch resources with fresh data
      const { data: resourcesData, error: resourcesError } = await supabase
        .from('resources_2025_12_03_10_30')
        .select('*')
        .eq('is_active', true)
        .order('updated_at', { ascending: false });

      if (resourcesError) {
        console.error('Error fetching resources:', resourcesError);
      }

      // Fetch courses with fresh data
      const { data: coursesData, error: coursesError } = await supabase
        .from('courses_2025_12_03_10_30')
        .select('*')
        .eq('is_active', true)
        .order('updated_at', { ascending: false });

      if (coursesError) {
        console.error('Error fetching courses:', coursesError);
      }

      // Fetch events with fresh data
      const { data: eventsData, error: eventsError } = await supabase
        .from('events_2025_12_03_10_30')
        .select('*')
        .eq('is_active', true)
        .gte('event_date', new Date().toISOString())
        .order('event_date', { ascending: true });

      if (eventsError) {
        console.error('Error fetching events:', eventsError);
      }

      // Fetch achievements with fresh data
      const { data: achievementsData, error: achievementsError } = await supabase
        .from('achievements_2025_12_03_10_30')
        .select('*')
        .order('points', { ascending: false });

      if (achievementsError) {
        console.error('Error fetching achievements:', achievementsError);
      }

      // Fetch user achievements with fresh data
      const { data: userAchievementsData, error: userAchievementsError } = await supabase
        .from('user_achievements_2025_12_03_10_30')
        .select(`
          id,
          user_id,
          achievement_id,
          earned_date,
          achievements_2025_12_03_10_30 (
            id,
            title,
            description,
            points
          )
        `)
        .eq('user_id', user.id)
        .order('earned_date', { ascending: false });

      if (userAchievementsError) {
        console.error('Error fetching user achievements:', userAchievementsError);
      }

      // Fetch certificates with fresh data
      const { data: certificatesData, error: certificatesError } = await supabase
        .from('certificates_2025_12_03_10_30')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (certificatesError) {
        console.error('Error fetching certificates:', certificatesError);
      }

      // Fetch badges with fresh data
      const { data: badgesData, error: badgesError } = await supabase
        .from('badges_2025_12_03_10_30')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (badgesError) {
        console.error('Error fetching badges:', badgesError);
      }

      // Update state with fresh data
      setResources(resourcesData || []);
      setCourses(coursesData || []);
      setEvents(eventsData || []);
      setAchievements(achievementsData || []);
      setUserAchievements(userAchievementsData || []);
      setCertificates(certificatesData || []);
      setBadges(badgesData || []);

      // Fetch enrollment requests for current user
      const { data: enrollmentData, error: enrollmentError } = await supabase
        .from('course_enrollment_requests_2025_12_03_12_00')
        .select('*')
        .eq('user_email', user?.email)
        .order('created_at', { ascending: false });

      if (enrollmentError) {
        console.error('Error fetching enrollment requests:', enrollmentError);
      } else {
        setEnrollmentRequests(enrollmentData || []);
      }

      // Fetch general enquiries for current user
      const { data: enquiriesData, error: enquiriesError } = await supabase
        .from('general_enquiries_2025_12_03_13_00')
        .select('*')
        .eq('user_email', user?.email)
        .order('created_at', { ascending: false });

      if (enquiriesError) {
        console.error('Error fetching general enquiries:', enquiriesError);
      } else {
        setGeneralEnquiries(enquiriesData || []);
      }

      // Refresh user achievements after any activity
      if (user?.email) {
        try {
          const { data: refreshResult, error: refreshError } = await supabase.rpc('refresh_user_achievements', {
            target_user_email: user.email
          });
          
          if (refreshError) {
            console.error('Error refreshing achievements:', refreshError);
          } else {
            console.log('Achievement refresh result:', refreshResult);
            if (refreshResult?.achievements_awarded > 0) {
              toast({
                title: "New Achievement!",
                description: `You've earned ${refreshResult.achievements_awarded} new achievement(s)!`,
              });
            }
          }
        } catch (error) {
          console.error('Error calling refresh_user_achievements:', error);
        }
      }
      
      console.log('Content refreshed:', {
        resources: resourcesData?.length || 0,
        courses: coursesData?.length || 0,
        events: eventsData?.length || 0,
        achievements: achievementsData?.length || 0,
        enrollmentRequests: enrollmentData?.length || 0,
        generalEnquiries: enquiriesData?.length || 0
      });
      
      // Debug: Log sample data
      if (resourcesData?.length > 0) {
        console.log('Sample resource:', resourcesData[0]);
      }
      if (coursesData?.length > 0) {
        console.log('Sample course:', coursesData[0]);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const handleDeleteEnquiry = async (enquiryId: string) => {
    console.log('Deleting enquiry:', enquiryId, 'for user:', user?.email);
    try {
      const { error } = await supabase
        .from('general_enquiries_2025_12_03_13_00')
        .delete()
        .eq('id', enquiryId)
        .eq('user_email', user?.email); // Ensure user can only delete their own enquiries

      if (error) {
        throw error;
      }

      // Refresh the enquiries list
      await fetchContent();
      
      toast({
        title: "Enquiry Deleted",
        description: "Your enquiry has been successfully deleted.",
      });
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete enquiry. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteEnrollmentRequest = async (requestId: string) => {
    console.log('Deleting enrollment request:', requestId, 'for user:', user?.email);
    try {
      const { error } = await supabase
        .from('course_enrollment_requests_2025_12_03_12_00')
        .delete()
        .eq('id', requestId)
        .eq('user_email', user?.email); // Ensure user can only delete their own requests

      if (error) {
        throw error;
      }

      // Refresh the enrollment requests list
      await fetchContent();
      
      toast({
        title: "Request Deleted",
        description: "Your enrollment request has been successfully deleted.",
      });
    } catch (error) {
      console.error('Error deleting enrollment request:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete enrollment request. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchProfile(),
        fetchContent()
      ]);
      toast({
        title: "Dashboard Refreshed",
        description: "All content has been updated with the latest data.",
      });
    } catch (error) {
      console.error('Error refreshing:', error);
      toast({
        title: "Refresh Failed",
        description: "Unable to refresh dashboard. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleChangePassword = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to change your password.",
        variant: "destructive",
      });
      return;
    }

    const newPassword = prompt("Enter your new password (minimum 6 characters):");
    
    if (!newPassword) {
      console.log('Password change cancelled');
      return;
    }
    
    if (newPassword.length < 6) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      console.log('Changing password for user:', user.id);
      
      const { data, error } = await supabase.functions.invoke('user_change_password_2025_12_04_14_00', {
        body: {
          userId: user.id,
          newPassword: newPassword
        }
      });
      
      console.log('Change password response:', { data, error });
      
      if (error) {
        console.error('Edge function error:', error);
        throw new Error(`Password change failed: ${error.message}`);
      }

      if (!data || !data.success) {
        console.error('Edge function returned failure:', data);
        throw new Error(data?.error || 'Unknown error occurred');
      }
      
      console.log('Password changed successfully:', data.message);
      
      toast({
        title: "Password Changed",
        description: data.message,
      });
      
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Password Change Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Calculate membership status based on dates with fallback to database status
  const getMembershipStatusByDates = (startDate?: string, endDate?: string, fallbackStatus?: string) => {
    // If no start date, use fallback status or default to database status
    if (!startDate) return fallbackStatus || 'active';
    
    try {
      const start = new Date(startDate);
      const today = new Date();
      
      // If start date is in the future
      if (start > today) return 'pending';
      
      // If end date is set and in the past
      if (endDate) {
        const end = new Date(endDate);
        if (end < today) return 'expired';
      }
      
      return 'active';
    } catch (error) {
      // If date parsing fails, use fallback status
      console.warn('Date parsing error:', error);
      return fallbackStatus || 'active';
    }
  };

  // Create display profile with fallback data
  const displayProfile = profile || {
    full_name: user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Member',
    email: user?.email || '',
    organization: 'Not specified',
    position: 'Not specified',
    phone: 'Not specified',
    country: 'Not specified',
    membership_status: 'active',
    payment_status: 'paid',
    membership_start_date: new Date().toISOString().split('T')[0],
    membership_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    membership_types_2025_12_03_10_30: {
      name: 'Professional Member',
      price_eur: 99.00,
      benefits: ['Full access to CSI resources', 'Networking opportunities', 'Professional development'],
      discount_percentage: 15
    }
  };

  // Debug: Log member profile
  console.log('Member profile:', displayProfile);
  console.log('Member type:', displayProfile.membership_types_2025_12_03_10_30?.name);

  // Payment links based on membership type
  const getPaymentLink = (membershipType: string) => {
    // First try to get payment link from database (if available)
    if (displayProfile.membership_types_2025_12_03_10_30?.payment_link) {
      return displayProfile.membership_types_2025_12_03_10_30.payment_link;
    }
    
    // Fallback to hardcoded links for existing membership types
    const paymentLinks = {
      'Student Member': 'https://pay.sumup.com/b2c/QBZZ305Y',
      'Professional Member': 'https://pay.sumup.com/b2c/QWJDPRCO',
      'Academic Member': 'https://pay.sumup.com/b2c/QST87DIO',
      'Chartered Member': 'https://pay.sumup.com/b2c/QCP7RV2Q',
      'Corporate Gold': 'https://pay.sumup.com/b2c/QBQ8RXZ5',
      'Corporate Silver': 'https://pay.sumup.com/b2c/QE6V08LM',
      'Corporate Bronze': 'https://pay.sumup.com/b2c/Q1ZU8XPS',
      'Training Centre': 'https://pay.sumup.com/b2c/QA4C61DF'
    };
    return paymentLinks[membershipType as keyof typeof paymentLinks] || null;
  };

  const currentPaymentLink = getPaymentLink(displayProfile.membership_types_2025_12_03_10_30?.name || '');
  const showPaymentLink = displayProfile.payment_status === 'pending' || displayProfile.payment_status === 'overdue' || displayProfile.payment_status === 'unpaid';

  const getDiscountedPrice = (originalPrice: number, membershipType: string) => {
    if (displayProfile.membership_types_2025_12_03_10_30?.name === membershipType) {
      const discount = displayProfile.membership_types_2025_12_03_10_30.discount_percentage || 0;
      return originalPrice * (1 - discount / 100);
    }
    return originalPrice;
  };

  const canAccessResource = (resource: Resource) => {
    // If no membership types specified, allow access to all members
    if (!resource.membership_types || resource.membership_types.length === 0) {
      console.log('Resource accessible (no membership restriction):', resource.title);
      return true;
    }
    const hasAccess = resource.membership_types.includes(displayProfile.membership_types_2025_12_03_10_30?.name || '');
    console.log('Resource access check:', {
      title: resource.title,
      requiredTypes: resource.membership_types,
      userType: displayProfile.membership_types_2025_12_03_10_30?.name,
      hasAccess
    });
    return hasAccess;
  };

  const canAccessCourse = (course: Course) => {
    // If no membership types specified, allow access to all members
    if (!course.membership_types || course.membership_types.length === 0) {
      console.log('Course accessible (no membership restriction):', course.title);
      return true;
    }
    const hasAccess = course.membership_types.includes(displayProfile.membership_types_2025_12_03_10_30?.name || '');
    console.log('Course access check:', {
      title: course.title,
      requiredTypes: course.membership_types,
      userType: displayProfile.membership_types_2025_12_03_10_30?.name,
      hasAccess
    });
    return hasAccess;
  };

  const canAccessEvent = (event: Event) => {
    // If no membership types specified, allow access to all members
    if (!event.membership_types || event.membership_types.length === 0) {
      console.log('Event accessible (no membership restriction):', event.title);
      return true;
    }
    const hasAccess = event.membership_types.includes(displayProfile.membership_types_2025_12_03_10_30?.name || '');
    console.log('Event access check:', {
      title: event.title,
      requiredTypes: event.membership_types,
      userType: displayProfile.membership_types_2025_12_03_10_30?.name,
      hasAccess
    });
    return hasAccess;
  };

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center">
        <div className="text-center">
          <User className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-lg font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Welcome, {displayProfile.full_name}!</h1>
            <p className="text-muted-foreground">Your CSI membership dashboard</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={handleRefresh} 
              variant="outline" 
              disabled={refreshing}
              className="flex items-center gap-2"
            >
              <Settings className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button onClick={handleLogout} variant="outline" className="flex items-center gap-2">
              <LogOut className="h-4 w-4" />
            Logout
          </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-8">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="resources">Resources</TabsTrigger>
            <TabsTrigger value="courses">Courses</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="achievements">Achievements</TabsTrigger>
            <TabsTrigger value="enrollments">
              Enrollments
              {enrollmentRequests.filter(req => req.status === 'pending').length > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">
                  {enrollmentRequests.filter(req => req.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="enquiries">
              Enquiries
              {generalEnquiries.filter(req => req.status === 'pending').length > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">
                  {generalEnquiries.filter(req => req.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Payment Alert */}
            {showPaymentLink && currentPaymentLink && (
              <Card className="border-yellow-200 bg-yellow-50">
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Bell className="h-5 w-5 text-yellow-600" />
                    <CardTitle className="text-yellow-800">Payment Required</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-yellow-700 mb-2">
                        Your {displayProfile.membership_types_2025_12_03_10_30?.name} membership payment is {displayProfile.payment_status}.
                      </p>
                      <p className="text-sm text-yellow-600">
                        Complete your payment to maintain full access to all membership benefits.
                      </p>
                    </div>
                    <Button 
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                      asChild
                    >
                      <a href={currentPaymentLink} target="_blank" rel="noopener noreferrer">
                        <CreditCard className="h-4 w-4 mr-2" />
                        Pay Now
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Membership Status</CardTitle>
                  <CheckCircle className={`h-4 w-4 ${
                    getMembershipStatusByDates(displayProfile.membership_start_date, displayProfile.membership_end_date, displayProfile.membership_status) === 'active' ? 'text-green-600' : 
                    getMembershipStatusByDates(displayProfile.membership_start_date, displayProfile.membership_end_date, displayProfile.membership_status) === 'pending' ? 'text-yellow-600' : 'text-red-600'
                  }`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold capitalize">{getMembershipStatusByDates(displayProfile.membership_start_date, displayProfile.membership_end_date, displayProfile.membership_status)}</div>
                  <p className="text-xs text-muted-foreground">
                    {displayProfile.membership_types_2025_12_03_10_30?.name}
                  </p>
                  {showPaymentLink && currentPaymentLink && (
                    <div className="mt-3">
                      <Button 
                        size="sm" 
                        className="w-full" 
                        asChild
                      >
                        <a href={currentPaymentLink} target="_blank" rel="noopener noreferrer">
                          <CreditCard className="h-4 w-4 mr-2" />
                          Pay Now
                        </a>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Payment Status</CardTitle>
                  <CreditCard className={`h-4 w-4 ${
                    displayProfile.payment_status === 'paid' ? 'text-green-600' : 
                    displayProfile.payment_status === 'pending' ? 'text-yellow-600' : 
                    displayProfile.payment_status === 'unpaid' ? 'text-orange-600' : 'text-red-600'
                  }`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold capitalize">{displayProfile.payment_status}</div>
                  <p className="text-xs text-muted-foreground">
                    {displayProfile.payment_status === 'paid' ? 'All payments up to date' : 
                     displayProfile.payment_status === 'pending' ? 'Payment required' : 
                     displayProfile.payment_status === 'unpaid' ? 'Payment required' : 'Payment overdue'}
                  </p>
                  {showPaymentLink && currentPaymentLink && (
                    <div className="mt-3">
                      <Button 
                        size="sm" 
                        variant="default"
                        className="w-full bg-blue-600 hover:bg-blue-700" 
                        asChild
                      >
                        <a href={currentPaymentLink} target="_blank" rel="noopener noreferrer">
                          <CreditCard className="h-4 w-4 mr-2" />
                          Complete Payment
                        </a>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Available Resources</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{resources.filter(canAccessResource).length}</div>
                  <p className="text-xs text-muted-foreground">Downloadable resources</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Achievements Earned</CardTitle>
                  <Trophy className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{userAchievements.length}</div>
                  <p className="text-xs text-muted-foreground">Total points: {userAchievements.reduce((sum, ua) => sum + (ua.achievements_2025_12_03_10_30?.points || 0), 0)}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Upcoming Events</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{events.filter(canAccessEvent).length}</div>
                  <p className="text-xs text-muted-foreground">Available to attend</p>
                </CardContent>
              </Card>
            </div>

            {/* Enrollment Requests Notifications */}
            {enrollmentRequests.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    Course Enrollment Status
                    {enrollmentRequests.filter(req => req.status === 'pending').length > 0 && (
                      <Badge variant="secondary">
                        {enrollmentRequests.filter(req => req.status === 'pending').length} Pending
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>Updates on your course enrollment requests</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {enrollmentRequests.slice(0, 3).map((request) => (
                      <div key={request.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`h-3 w-3 rounded-full ${
                            request.status === 'pending' ? 'bg-yellow-500 animate-pulse' :
                            request.status === 'approved' ? 'bg-green-500' :
                            request.status === 'rejected' ? 'bg-red-500' : 'bg-gray-500'
                          }`} />
                          <div>
                            <p className="font-medium text-sm">{request.course_title}</p>
                            <p className="text-xs text-muted-foreground">
                              {request.status === 'pending' && 'Waiting for approval'}
                              {request.status === 'approved' && 'Approved - Check your email'}
                              {request.status === 'rejected' && 'Request not approved'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge 
                            variant={
                              request.status === 'pending' ? 'secondary' :
                              request.status === 'approved' ? 'default' :
                              request.status === 'rejected' ? 'destructive' : 'outline'
                            }
                            className={`text-xs ${
                              request.status === 'approved' ? 'bg-green-100 text-green-800 border-green-300' :
                              request.status === 'rejected' ? 'bg-red-100 text-red-800 border-red-300' : ''
                            }`}
                          >
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            €{request.discounted_price?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {enrollmentRequests.length > 3 && (
                      <div className="text-center pt-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setActiveTab('enrollments')}
                        >
                          View All ({enrollmentRequests.length}) Requests
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* General Enquiry Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Need Help or Have Questions?
                </CardTitle>
                <CardDescription>
                  Submit a general enquiry about resources, events, achievements, profile, or any other questions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Our admin team is here to help! Submit your questions about:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <FileText className="h-3 w-3" />
                      Resources
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      Events
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Award className="h-3 w-3" />
                      Achievements
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <User className="h-3 w-3" />
                      Profile
                    </div>
                  </div>
                  
                  {/* Show recent enquiries if any */}
                  {generalEnquiries.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium mb-2">Your Recent Enquiries</h4>
                      <div className="space-y-2">
                        {generalEnquiries.slice(0, 2).map((enquiry) => (
                          <div key={enquiry.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium">{enquiry.subject}</p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {enquiry.enquiry_type} • {new Date(enquiry.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge 
                              variant={
                                enquiry.status === 'pending' ? 'secondary' :
                                enquiry.status === 'in_progress' ? 'default' :
                                enquiry.status === 'resolved' ? 'default' : 'outline'
                              }
                              className={`text-xs ${
                                enquiry.status === 'resolved' ? 'bg-green-100 text-green-800 border-green-300' :
                                enquiry.status === 'in_progress' ? 'bg-blue-100 text-blue-800 border-blue-300' : ''
                              }`}
                            >
                              {enquiry.status.replace('_', ' ')}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <GeneralEnquiryForm 
                    userProfile={{
                      full_name: displayProfile.full_name,
                      email: displayProfile.email,
                      organization: displayProfile.organization,
                      position: displayProfile.position
                    }}
                    onEnquirySubmitted={() => {
                      // Refresh enquiries after submission
                      fetchContent();
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Recent Achievements */}
            {userAchievements.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Recent Achievements</CardTitle>
                  <CardDescription>Your latest accomplishments</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {userAchievements.slice(0, 3).map((userAchievement) => (
                      <div key={userAchievement.id} className="flex items-center space-x-4 p-4 border rounded-lg">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                          <Award className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium">{userAchievement.achievements_2025_12_03_10_30?.title}</h4>
                          <p className="text-sm text-muted-foreground">
                            {userAchievement.achievements_2025_12_03_10_30?.points} points
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(userAchievement.earned_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Certificates and Badges */}
            {(certificates.length > 0 || badges.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {certificates.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Your Certificates</CardTitle>
                      <CardDescription>Download your earned certificates</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {certificates.map((certificate) => (
                          <div key={certificate.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div>
                              <h4 className="font-medium">{certificate.certificate_name}</h4>
                              <p className="text-sm text-muted-foreground">
                                Issued: {new Date(certificate.issue_date).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Code: {certificate.verification_code}
                              </p>
                            </div>
                            <Button size="sm" variant="outline" asChild>
                              <a href={certificate.certificate_url} target="_blank" rel="noopener noreferrer">
                                <Download className="h-4 w-4 mr-2" />
                                Download
                              </a>
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {badges.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Your Badges</CardTitle>
                      <CardDescription>Your earned digital badges</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        {badges.map((badge) => (
                          <div key={badge.id} className="text-center p-4 border rounded-lg">
                            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                              <Award className="h-8 w-8 text-primary" />
                            </div>
                            <h4 className="font-medium text-sm">{badge.badge_name}</h4>
                            <p className="text-xs text-muted-foreground">
                              {new Date(badge.earned_date).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Available Resources</CardTitle>
                <CardDescription>Download resources available to your membership level</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {resources.filter(canAccessResource).map((resource) => (
                    <Card key={resource.id}>
                      <CardHeader>
                        <CardTitle className="text-lg">{resource.title}</CardTitle>
                        <CardDescription>{resource.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div>
                            <Badge variant="outline">{resource.file_type}</Badge>
                            <p className="text-sm text-muted-foreground mt-1">{resource.category}</p>
                          </div>
                          <Button size="sm" asChild>
                            <a href={resource.file_url} target="_blank" rel="noopener noreferrer">
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </a>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {resources.filter(canAccessResource).length === 0 && (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No resources available for your membership level</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Available Courses</CardTitle>
                <CardDescription>Training courses with member discounts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {courses.filter(canAccessCourse).map((course) => {
                    const discountedPrice = getDiscountedPrice(course.price_eur, displayProfile.membership_types_2025_12_03_10_30?.name || '');
                    const hasDiscount = discountedPrice < course.price_eur;
                    
                    return (
                      <Card key={course.id}>
                        <CardHeader>
                          <CardTitle className="text-lg">{course.title}</CardTitle>
                          <CardDescription>{course.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Instructor:</span>
                              <span className="text-sm font-medium">{course.instructor}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Duration:</span>
                              <span className="text-sm font-medium">{course.duration_hours}h</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Level:</span>
                              <Badge variant="outline">{course.difficulty_level}</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Price:</span>
                              <div className="text-right">
                                {hasDiscount && (
                                  <span className="text-sm text-muted-foreground line-through">€{course.price_eur}</span>
                                )}
                                <span className="text-sm font-medium ml-2">€{discountedPrice.toFixed(2)}</span>
                                {hasDiscount && (
                                  <Badge variant="secondary" className="ml-2">
                                    {displayProfile.membership_types_2025_12_03_10_30?.discount_percentage}% OFF
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="mt-4">
                              <CourseEnrollmentForm 
                                course={course}
                                userProfile={displayProfile}
                                discountedPrice={discountedPrice}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                {courses.filter(canAccessCourse).length === 0 && (
                  <div className="text-center py-8">
                    <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No courses available for your membership level</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Events</CardTitle>
                <CardDescription>Events and webinars available to your membership</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {events.filter(canAccessEvent).map((event) => (
                    <Card key={event.id}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg">{event.title}</CardTitle>
                            <CardDescription>{event.description}</CardDescription>
                          </div>
                          <Badge variant="outline">{event.event_type}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              {event.event_date ? new Date(event.event_date).toLocaleDateString() : 'TBD'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              {event.event_date ? new Date(event.event_date).toLocaleTimeString() : 'TBD'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{event.location}</span>
                          </div>
                        </div>
                        <Button className="w-full mt-4" asChild>
                          <a href={event.registration_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Register Now
                          </a>
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {events.filter(canAccessEvent).length === 0 && (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No upcoming events available for your membership level</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Achievements Tab */}
          <TabsContent value="achievements" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Available Achievements */}
              <Card>
                <CardHeader>
                  <CardTitle>Available Achievements</CardTitle>
                  <CardDescription>Badges you can earn</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {achievements.filter(achievement => 
                      !userAchievements.some(ua => ua.achievement_id === achievement.id)
                    ).map((achievement) => (
                      <div key={achievement.id} className="flex items-center space-x-4 p-4 border rounded-lg">
                        <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                          <Award className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{achievement.title}</h4>
                          <p className="text-sm text-muted-foreground">{achievement.description}</p>
                          <p className="text-xs text-muted-foreground mt-1">{achievement.criteria}</p>
                        </div>
                        <Badge variant="outline">{achievement.points} pts</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Earned Achievements */}
              <Card>
                <CardHeader>
                  <CardTitle>Earned Achievements</CardTitle>
                  <CardDescription>Your accomplishments</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {userAchievements.map((userAchievement) => (
                      <div key={userAchievement.id} className="flex items-center space-x-4 p-4 border rounded-lg bg-primary/5">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                          <Award className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{userAchievement.achievements_2025_12_03_10_30?.title}</h4>
                          <p className="text-sm text-muted-foreground">
                            {userAchievement.achievements_2025_12_03_10_30?.description}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Earned: {new Date(userAchievement.earned_date).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge>{userAchievement.achievements_2025_12_03_10_30?.points} pts</Badge>
                      </div>
                    ))}
                  </div>
                  {userAchievements.length === 0 && (
                    <div className="text-center py-8">
                      <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No achievements earned yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Enrollment Requests Tab */}
          <TabsContent value="enrollments" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  My Course Enrollment Requests
                  {enrollmentRequests.filter(req => req.status === 'pending').length > 0 && (
                    <Badge variant="destructive">
                      {enrollmentRequests.filter(req => req.status === 'pending').length} Pending
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Track your course enrollment requests and their approval status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {enrollmentRequests.map((request) => (
                    <div key={request.id} className="border rounded-lg p-6 space-y-4">
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <h3 className="font-semibold text-lg">{request.course_title}</h3>
                          <p className="text-sm text-muted-foreground">
                            Instructor: {request.course_instructor}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Submitted: {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge 
                          variant={
                            request.status === 'pending' ? 'secondary' :
                            request.status === 'approved' ? 'default' :
                            request.status === 'rejected' ? 'destructive' : 'outline'
                          }
                          className={`${
                            request.status === 'approved' ? 'bg-green-100 text-green-800 border-green-300' :
                            request.status === 'rejected' ? 'bg-red-100 text-red-800 border-red-300' : ''
                          }`}
                        >
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Original Price</p>
                          <p className="text-lg line-through text-muted-foreground">
                            €{request.original_price?.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Member Discount</p>
                          <p className="text-lg font-semibold text-green-600">
                            {request.discount_percentage}% OFF
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Your Price</p>
                          <p className="text-xl font-bold text-green-600">
                            €{request.discounted_price?.toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {request.additional_notes && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm font-medium text-blue-800 mb-1">Your Notes:</p>
                          <p className="text-sm text-blue-700">{request.additional_notes}</p>
                        </div>
                      )}

                      <div className="flex justify-between items-center pt-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          {request.status === 'pending' && (
                            <span className="flex items-center gap-1">
                              <div className="h-2 w-2 bg-yellow-500 rounded-full animate-pulse"></div>
                              Waiting for admin approval
                            </span>
                          )}
                          {request.status === 'approved' && (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              Approved! Check your email for next steps
                            </span>
                          )}
                          {request.status === 'rejected' && (
                            <span className="flex items-center gap-1 text-red-600">
                              <XCircle className="h-4 w-4" />
                              Request was not approved
                            </span>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          {request.status === 'pending' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                console.log('Delete enrollment button clicked:', request.id);
                                handleDeleteEnrollmentRequest(request.id);
                              }}
                            >
                              Cancel Request
                            </Button>
                          )}
                          
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => {
                              const subject = encodeURIComponent(`Question about ${request.course_title} enrollment`);
                              const body = encodeURIComponent(
                                `Hello,\n\n` +
                                `I have a question about my enrollment request for ${request.course_title}.\n\n` +
                                `Request Status: ${request.status}\n` +
                                `Submitted: ${new Date(request.created_at).toLocaleDateString()}\n\n` +
                                `Please let me know if you need any additional information.\n\n` +
                                `Best regards,\n${request.user_name}`
                              );
                              window.open(`mailto:info@converged.security?subject=${subject}&body=${body}`);
                            }}
                          >
                            Contact Admin
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {enrollmentRequests.length === 0 && (
                    <div className="text-center py-12">
                      <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                        No Enrollment Requests Yet
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        You haven't submitted any course enrollment requests.
                      </p>
                      <Button 
                        onClick={() => {
                          // Switch to courses tab
                          setActiveTab('courses');
                        }}
                      >
                        Browse Courses
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Personal Information</CardTitle>
                      <CardDescription>Your account details</CardDescription>
                    </div>
                    <Button 
                      onClick={handleChangePassword}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                      title="Change your password"
                    >
                      <Key className="h-4 w-4" />
                      Change Password
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Full Name</label>
                    <p className="text-sm text-muted-foreground">{displayProfile.full_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email</label>
                    <p className="text-sm text-muted-foreground">{displayProfile.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Organization</label>
                    <p className="text-sm text-muted-foreground">{displayProfile.organization}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Position</label>
                    <p className="text-sm text-muted-foreground">{displayProfile.position}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Country</label>
                    <p className="text-sm text-muted-foreground">{displayProfile.country}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Membership Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Membership Information</CardTitle>
                  <CardDescription>Your membership details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Membership Type</label>
                    <p className="text-sm text-muted-foreground">
                      {displayProfile.membership_types_2025_12_03_10_30?.name}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Status</label>
                    <Badge variant={getMembershipStatusByDates(displayProfile.membership_start_date, displayProfile.membership_end_date, displayProfile.membership_status) === 'active' ? 'default' : getMembershipStatusByDates(displayProfile.membership_start_date, displayProfile.membership_end_date, displayProfile.membership_status) === 'expired' ? 'destructive' : 'secondary'}>
                      {getMembershipStatusByDates(displayProfile.membership_start_date, displayProfile.membership_end_date, displayProfile.membership_status)}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Payment Status</label>
                    <Badge variant={displayProfile.payment_status === 'paid' ? 'default' : displayProfile.payment_status === 'unpaid' ? 'secondary' : 'destructive'}>
                      {displayProfile.payment_status}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Member Since</label>
                    <p className="text-sm text-muted-foreground">
                      {new Date(displayProfile.membership_start_date).toLocaleDateString()}
                    </p>
                  </div>
                  {displayProfile.membership_end_date && (
                    <div>
                      <label className="text-sm font-medium">Membership Expires</label>
                      <p className="text-sm text-muted-foreground">
                        {new Date(displayProfile.membership_end_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium">Membership Benefits</label>
                    <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                      {displayProfile.membership_types_2025_12_03_10_30?.benefits?.map((benefit, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Enquiries Tab */}
          <TabsContent value="enquiries" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Your General Enquiries
                </CardTitle>
                <CardDescription>
                  View and manage your submitted enquiries
                </CardDescription>
              </CardHeader>
              <CardContent>
                {generalEnquiries.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No enquiries submitted yet</p>
                    <Button 
                      onClick={() => setActiveTab('overview')} 
                      variant="outline" 
                      className="mt-4"
                    >
                      Submit Your First Enquiry
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {generalEnquiries.map((enquiry) => (
                      <Card key={enquiry.id} className="border-l-4 border-l-blue-500">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <CardTitle className="text-lg">{enquiry.subject}</CardTitle>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span className="capitalize">{enquiry.enquiry_type}</span>
                                <span>•</span>
                                <span className="capitalize">{enquiry.priority} Priority</span>
                                <span>•</span>
                                <span>{new Date(enquiry.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge 
                                variant={
                                  enquiry.status === 'pending' ? 'secondary' :
                                  enquiry.status === 'in_progress' ? 'default' :
                                  enquiry.status === 'resolved' ? 'default' : 'outline'
                                }
                                className={`${
                                  enquiry.status === 'resolved' ? 'bg-green-100 text-green-800 border-green-300' :
                                  enquiry.status === 'in_progress' ? 'bg-blue-100 text-blue-800 border-blue-300' : ''
                                }`}
                              >
                                {enquiry.status.replace('_', ' ')}
                              </Badge>
                              {enquiry.status === 'pending' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    console.log('Delete enquiry button clicked:', enquiry.id);
                                    handleDeleteEnquiry(enquiry.id);
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="space-y-3">
                            <div>
                              <p className="text-sm text-muted-foreground mb-2">Message:</p>
                              <p className="text-sm bg-gray-50 p-3 rounded-lg">{enquiry.message}</p>
                            </div>
                            {enquiry.admin_response && (
                              <div>
                                <p className="text-sm font-medium text-green-700 mb-2">Admin Response:</p>
                                <p className="text-sm bg-green-50 p-3 rounded-lg border border-green-200">
                                  {enquiry.admin_response}
                                </p>
                              </div>
                            )}
                            {enquiry.admin_notes && (
                              <div>
                                <p className="text-sm font-medium text-blue-700 mb-2">Admin Notes:</p>
                                <p className="text-sm bg-blue-50 p-3 rounded-lg border border-blue-200">
                                  {enquiry.admin_notes}
                                </p>
                              </div>
                            )}
                            <div className="flex items-center justify-between pt-2 border-t">
                              <div className="text-xs text-muted-foreground">
                                {enquiry.resolved_at && (
                                  <span>Resolved on {new Date(enquiry.resolved_at).toLocaleDateString()}</span>
                                )}
                              </div>
                              {enquiry.status !== 'resolved' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  asChild
                                >
                                  <a href={`mailto:info@converged.security?subject=Re: ${enquiry.subject}&body=Original enquiry: ${enquiry.message}`}>
                                    <MessageSquare className="h-4 w-4 mr-2" />
                                    Contact Admin
                                  </a>
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EnhancedMemberDashboard;